import { action } from '@play-co/replicant';
import createActions from '../config/actions/createActions';
import { getJSTDateTime } from '../utils/time';
import { Feature, sendAnalyticsEvents } from '../analytics';
import { getVisitedCount, isURLFriend } from './visit.getters';
import { incrementGemsGrants, incrementVisitsCount } from './visit.modifiers';
import * as Inventory from '../inventory';
import * as Fate from '../fate';
import * as Encyclopedia from '../encyclopedia';
import { gemsReward, friendRevivalCost } from './visit.ruleset';
import { endVisitAction } from './visit.util';
import { getSharedStateActionsAPI } from '../config/actions/createSharedStatesActions';
import { isBuruFriend } from '../friends/friends.getters';
import { FriendVisitDetails } from './visit.asyncGetters';
import { PetAttribute, PetStatusEffect } from '../pet';
import * as Pet from '../pet';
import { getPetSpeciesConfig } from '../pet/pet.getters';

export const visitActions = createActions({
  beginVisit: action(
    (
      state,
      {
        petDetails,
        sourceFeature,
      }: { petDetails: FriendVisitDetails; sourceFeature?: string },
      api,
    ) => {
      const friendId = petDetails.id;
      if (!state.publicProfile && !isBuruFriend(state, friendId)) {
        throw new Error(
          `Can't visit a non-friend without setting a public profile`,
        );
      }

      // Create new visit session
      state.visit.visitSession = {
        friendId,
        name: petDetails.name ?? 'Unknown',
        photo: petDetails.photo ?? '',
        frame: petDetails.frame,
        lineName: petDetails.lineName,
        linePhoto: petDetails.linePhoto,
        revivedPet: petDetails.revivedPet ?? false,
        petName: petDetails.attributes.name,
        currentEvolution: {
          species: petDetails.currentEvolution.species,
          stage: petDetails.currentEvolution.stage,
        },
        careActions: 0,
        paidCareActions: 0,
        friendDialogs: 0,
        gemsReceived: 0,
        coinsSpent: 0,
        answers: petDetails.answers,
        unsyncedChanges: {
          attributes: {},
          statusEffects: {},
        },
        attributeDebuffs: {
          stomach: 0,
          love: 0,
        },
        features: petDetails.features,
      };

      // Increment counter of visits to friend
      const now = api.date.now();
      const visits = incrementVisitsCount(state, friendId, now);
      const visited = getVisitedCount(state, friendId);

      // Resolve 'want_to_go_out' effect.
      Fate.modifiers.resolveStatusEffect(state, 'wantToGoOut', false, now);

      // Determine new fate
      Fate.modifiers.update(state, now);

      // Post message to friend to track visit
      const { name } = state.publicProfile ??
        state.profile ?? { name: 'Unknown' };
      api.postMessage.trackVisit(friendId, { name: name ?? 'Unknown' });

      // Unveil pet in encyclopedia
      const { species } = petDetails.currentEvolution;
      const unveil = species;
      if (Encyclopedia.getters.getSpeciesState(state, unveil) === 'veiled') {
        Encyclopedia.modifiers.setSpeciesState(state, unveil, 'unveiled');
      }

      // Clear any scheduled status messages for this friend
      api.scheduledActions.unschedule({
        notificationId: `sendStatusChangedChatbotMessage`,
        targetUserId: friendId,
      });

      // Send analytics
      const { age, attributes } = petDetails;
      const friendPetIsDead =
        petDetails.state === 'dead' || petDetails.revivedPet === true;
      sendAnalyticsEvents(api, [
        {
          eventType: 'FriendVisitStart',
          eventProperties: {
            friendID: friendId,
            friendPetID: species,
            friendPetIsDead,
            friendAge: age,
            friendPetStomach: attributes.stomach,
            friendPetLove: attributes.love,
            friendDirtiness: attributes.dirtiness,
            friendUrine: attributes.urine,
            friendStress: attributes.stress,
            friendToothAcidity: attributes.toothAcidity,
            visits,
            visited,
            friend: isBuruFriend(state, friendId),
            urlInviteFriend: isURLFriend(state, friendId),
            sourceFeature,
          },
        },
      ]);
    },
  ),

  endVisit: action((state, sendMessage: boolean, api) =>
    endVisitAction(
      () => delete state.visit.visitSession,
      state,
      api,
      sendMessage,
    ),
  ),

  syncFriendState: action(
    (
      state,
      {
        attributes,
        statusEffects,
      }: {
        attributes: Record<PetAttribute, number>;
        statusEffects: Partial<Record<PetStatusEffect, boolean>>;
      },
      api,
    ): {
      granted: boolean;
      attributes: Record<PetAttribute, number>;
      statusEffects: Partial<Record<PetStatusEffect, boolean>>;
    } => {
      if (!state.visit.visitSession) {
        throw new Error(`No active visit session`);
      }

      const { friendId, attributeDebuffs } = state.visit.visitSession ?? {};
      const { unsyncedChanges } = state.visit.visitSession;

      if (!state.publicProfile && !isBuruFriend(state, friendId)) {
        throw new Error(
          `Can't visit a non-friend without setting a public profile`,
        );
      }

      api.postMessage.updateFate(friendId, {
        now: api.date.now(),
        effects: unsyncedChanges.statusEffects,
        attributes: unsyncedChanges.attributes,
      });

      if (unsyncedChanges.attributes['love']) {
        attributeDebuffs.love += unsyncedChanges.attributes['love'];
      }

      if (unsyncedChanges.attributes['stomach']) {
        attributeDebuffs.stomach += unsyncedChanges.attributes['stomach'];
      }

      state.visit.visitSession.unsyncedChanges = {
        attributes: {},
        statusEffects: {},
      };

      const newAttributes = JSON.parse(
        JSON.stringify(attributes),
      ) as typeof attributes;
      Object.entries(unsyncedChanges.attributes).forEach(([rawKey, value]) => {
        const key = rawKey as keyof typeof attributes;
        newAttributes[key] += value;
        // Clamp attributes to 0-100
        newAttributes[key] = Math.min(100, Math.max(0, newAttributes[key]));
      });

      const newStatusEffects = JSON.parse(
        JSON.stringify(statusEffects),
      ) as typeof statusEffects;
      Object.entries(unsyncedChanges.statusEffects).forEach(
        ([key, value]) =>
          (newStatusEffects[key as keyof typeof statusEffects] = value),
      );

      const oldState = Pet.ruleset.getPetState(attributes, statusEffects, true);
      const newState = Pet.ruleset.getPetState(
        newAttributes,
        newStatusEffects,
        true,
      );
      // Pet was already happy or didn't become happy, so no gems are granted.
      if (oldState === 'happy' || newState !== 'happy') {
        return {
          granted: false,
          attributes: newAttributes,
          statusEffects: newStatusEffects,
        };
      }

      const teamId = state.team.currentTeamId;
      if (
        teamId &&
        ['sick', 'dying'].includes(oldState) &&
        !['sick', 'dying'].includes(newState)
      ) {
        getSharedStateActionsAPI(
          api,
        ).sharedStates.team.postMessage.sendCareHelp(teamId, {
          targetId: friendId,
          timestamp: api.date.now(),
        });
      }

      // Get JST date for today.
      const jstDay = getJSTDateTime(api.date.now()).startOf('day').valueOf();

      const { gemGrants, lastGemsGrantAt } = state.visit;
      const lastFriendGemsGrantAt = gemGrants[friendId] ?? 0;

      // Get friend's last gem grant JST date.
      const lastGrantFromFriend = getJSTDateTime(lastFriendGemsGrantAt)
        .startOf('day')
        .valueOf();

      // Can only get gems once per day. So if dates are the same, no gems are granted.
      if (jstDay === lastGrantFromFriend) {
        return {
          granted: false,
          attributes: newAttributes,
          statusEffects: newStatusEffects,
        };
      }

      // Get last gem grant JST date.
      const lastGrant = getJSTDateTime(lastGemsGrantAt)
        .startOf('day')
        .valueOf();

      // Reset counter if it's been more than a day since last grant.
      if (jstDay > lastGrant) {
        state.visit.gemsGrantsToday = 0;
      }

      // Get a random number.
      const random = api.math.random();
      // Chance decreases by 20% for every grant of the day.
      const chance = Math.max(0, 1 - state.visit.gemsGrantsToday * 0.2);

      // If roll is not successful, don't grant gems.
      if (random >= chance) {
        return {
          granted: false,
          attributes: newAttributes,
          statusEffects: newStatusEffects,
        };
      }

      // Update visit state.
      incrementGemsGrants(state, friendId, api.date.now());

      // Add pending gems.
      state.visit.pendingGemsGrants += gemsReward;

      return {
        granted: true,
        attributes: newAttributes,
        statusEffects: newStatusEffects,
      };
    },
  ),

  claimVisitGems: action((state, _, api) => {
    const { pendingGemsGrants } = state.visit;

    Inventory.modifiers.addFreeGems(
      state,
      pendingGemsGrants,
      { feature: Feature.VISIT._, $subFeature: Feature.VISIT.REWARD },
      api,
    );
    state.visit.pendingGemsGrants = 0;
  }),

  acknowledgeVisit: action((state, _: void, api) => {
    const now = api.date.now();
    Fate.modifiers.resolveStatusEffect(state, 'hadVisitor', false, now);
    Fate.modifiers.update(state, now);
  }),

  kickVisitors: action((state, visitors: string[], api) => {
    visitors?.forEach((id) => api.postMessage.kickVisitor(id, {}));
  }),

  reviveFriend: action((state, friendId: string, api) => {
    // First revive is free.
    const cost = state.visit.revivedFriends ? friendRevivalCost : 0;

    // Not enough gems, throw.
    if (Inventory.getters.getGems(state) < cost) {
      throw new Error(`Not enough gems to revive friend`);
    }

    // Deduct gems.
    if (cost) {
      Inventory.modifiers.consumeGems(
        state,
        cost,
        {
          feature: Feature.VISIT._,
          $subFeature: Feature.VISIT.REVIVE,
        },
        api,
      );
    }

    // Revive friend.
    // Reset attributes to ball species initial values.
    // That will make the pet hungry.
    const initialAttributes = getPetSpeciesConfig(state, 'ball');
    const fateDiff = {
      attributes: {
        love: initialAttributes.love_ini!,
        stomach: initialAttributes.stomach_ini!,
        dirtiness: initialAttributes.dirtiness_ini!,
        urine: initialAttributes.urine_ini!,
        toothAcidity: initialAttributes.tooth_acidity_ini!,
      },
      effects: { dead: false, dying: false, sick: false, scared: false },
    };
    api.postMessage.updateFate(friendId, {
      attributesMode: 'set',
      ...fateDiff,
      now: api.date.now(),
    });
    api.postMessage.revivePet(friendId, {
      friendName:
        state.publicProfile?.name ??
        (state.settings.locale === 'ja' ? 'お友だち' : 'A friend'),
    });

    // Increment revived friends count.
    state.visit.revivedFriends = (state.visit.revivedFriends ?? 0) + 1;

    return fateDiff;
  }),

  sendReviveOA: action((state, { friendId }: { friendId: string }, api) => {
    // Send OA.
    api.scheduledActions.schedule.sendFriendReviveNotification({
      args: { friendId: friendId },
      notificationId: `sendFriendReviveNotification-${friendId}`,
      delayInMS: 0,
    });
  }),

  sendBackToLifeOA: action((state, { friendId }: { friendId: string }, api) => {
    // Send OA.
    api.scheduledActions.schedule.sendFriendBackToLifeNotification({
      args: { friendId: friendId },
      notificationId: `sendFriendBackToLifeNotification-${friendId}`,
      delayInMS: 0,
    });
  }),

  markReviveNotificationAsRead: action((state) => {
    delete state.visit.revivedBy;
  }),
});

export type GrantGemsReturn = ReturnType<
  (typeof visitActions)['syncFriendState']['fn']
>;
